
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import vSelect from "vue-select";
import { difference, isEqual } from "lodash";
import { DeviceDetails } from "@sureview/v2-cameras";
import { UserGroup, Permission } from "@/store/user-management/types";
import api from "@/services/api.service";
import { licenseMixins } from "@/mixins";

const { isLicenseLimitUnlimited } = licenseMixins.methods;

const License = namespace("license");

@Component({
    components: {
        "v-select": vSelect,
    }
})
export default class UserGroupInput extends Vue {
    @Prop({ type: Boolean, default: false }) readonly: boolean;

    @Prop({ type: Boolean, default: false }) disabled: boolean;

    @Prop({ type: Boolean, default: false }) required: boolean;

    @Prop({ type: String, default: "User Group" }) parentTitle: String;

    @Prop()
    public value: any[];
    
    @Getter getPermissions: any;

    @License.Getter getLicenseGuardLimit: number;
    @License.Getter getLicenseOperatorLimit: number;
    @License.Getter getLicenseUserLimitDefinitions: any;
    
    @License.Action fetchLicensedUserPermissions: any;

    private userGroups: any[] = [];
    private selectedUserGroups: any[] = [];
    private selectedUserGroupPermissions: Permission[] = [];

    private initialOperatorUsage = 0;
    private initialGuardUsage = 0;
    private initialSelectedUserGroupIds: any[] = [];
    private permissionTitlesForInitialUserGroups: string[] = [];

    private operatorUsage = 0;
    private guardUsage = 0;

    private maximumUsageUpperLimit = 2000000000;

    private async mounted() {
        var getUserGroups = await api.getUserGroup();
        this.userGroups = getUserGroups.map(ug => {
            var result = {}
            result['key'] = ug.userGroupId;
            result['title'] = ug.title;
            return result;
        })

        if (this.value) {
            this.initialSelectedUserGroupIds = this.value.map(x => x.key);
            var permissionsForInitialUserGroups = await api.getPermissionsForUserGroups(this.initialSelectedUserGroupIds);
            this.permissionTitlesForInitialUserGroups = permissionsForInitialUserGroups.map(p => p.title);
            this.selectedUserGroups = this.value;
        }
        this.fetchLicensedUserPermissions();

        this.initialOperatorUsage = await api.getTotalOperatorUsage();
        this.initialGuardUsage = await api.getTotalGuardUsage();

        this.operatorUsage = this.initialOperatorUsage;
        this.guardUsage = this.initialGuardUsage;

        this.$emit("component-loaded");
    }

    @Watch("value")
    valueChanged(value: any[]) {
        this.selectedUserGroups = this.value;
    }

    @Watch("selectedUserGroups")
    public updateUserGroup(userGroupIds): void {
        this.$emit("input", userGroupIds);
    }

    private async onSelectUserGroup(userGroups: any[]) {
        if (!this.hasUnlimitedUserLicenses) {
            var userGroupIds = userGroups.map(x => x.key);
            var isUserGroupsUnchanged = isEqual(userGroupIds, this.initialSelectedUserGroupIds);
            if (isUserGroupsUnchanged) {
                this.guardUsage = this.initialGuardUsage;
                this.operatorUsage = this.initialOperatorUsage;
                return;
            }

            var permissionsForUserGroups = await api.getPermissionsForUserGroups(userGroupIds);
            var selectedUserGroupPermissions = permissionsForUserGroups.map(permission => permission.title);
            
            this.updateOperatorCount(selectedUserGroupPermissions);
            this.updateGuardCount(selectedUserGroupPermissions);
        }
    }

    private updateOperatorCount(selectedUserGroupPermissions) {
        var initialUserGroupsContainOperatorPermissions = difference(this.getLicenseUserLimitDefinitions.operatorPermissions, this.permissionTitlesForInitialUserGroups).length === 0;
        var selectedUserGroupContainsOperatorPermissions = difference(this.getLicenseUserLimitDefinitions.operatorPermissions, selectedUserGroupPermissions).length === 0;

        if (!initialUserGroupsContainOperatorPermissions && selectedUserGroupContainsOperatorPermissions && this.initialOperatorUsage + 1 > this.operatorUsage) {
            this.operatorUsage++;
        } else if (initialUserGroupsContainOperatorPermissions && 
                    !selectedUserGroupContainsOperatorPermissions &&
                    this.initialOperatorUsage === this.operatorUsage) {
            this.operatorUsage--;
        } else if (initialUserGroupsContainOperatorPermissions == selectedUserGroupContainsOperatorPermissions) {
            this.operatorUsage = this.initialOperatorUsage;
        }
    }

    private updateGuardCount(selectedUserGroupPermissions) {
        var initialUserGroupsContainGuardPermissions = difference(this.getLicenseUserLimitDefinitions.guardPermissions, this.permissionTitlesForInitialUserGroups).length === 0;
        var selectedUserGroupContainsGuardPermissions = difference(this.getLicenseUserLimitDefinitions.guardPermissions, selectedUserGroupPermissions).length === 0;

        if (!initialUserGroupsContainGuardPermissions && selectedUserGroupContainsGuardPermissions && this.initialGuardUsage + 1 > this.guardUsage) {
            this.guardUsage++;
        } else if (initialUserGroupsContainGuardPermissions && 
                !selectedUserGroupContainsGuardPermissions &&
                this.initialGuardUsage === this.guardUsage) {
            this.guardUsage--;
        } else if (initialUserGroupsContainGuardPermissions == selectedUserGroupContainsGuardPermissions) {
            this.guardUsage = this.initialGuardUsage;
        }
    }

    private get licenseOperatorLimit() {
        return isLicenseLimitUnlimited(this.getLicenseOperatorLimit) ? Infinity : this.getLicenseOperatorLimit;
    }

    private get licenseGuardLimit() {
        return isLicenseLimitUnlimited(this.getLicenseGuardLimit) ? Infinity : this.getLicenseGuardLimit;
    }

    private get hasUnlimitedUserLicenses() {
        return isLicenseLimitUnlimited(this.getLicenseGuardLimit) && isLicenseLimitUnlimited(this.getLicenseOperatorLimit);
    }

    private get operatorLicenseUsageBadgeVariant() {
        if (this.operatorUsage < this.licenseOperatorLimit) {
            return "secondary";
        } else if (this.operatorUsage == this.licenseOperatorLimit) {
            return "warning";
        }
        return "danger";
    }

    private get guardLicenseUsageBadgeVariant() {
        if (this.guardUsage < this.licenseGuardLimit) {
            return "secondary";
        } else if (this.guardUsage == this.licenseGuardLimit) {
            return "warning";
        }
        return "danger";
    }
}
